import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import Modal from "../../../components/Modal/Modal";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import Loader from "../../../components/Loader";

const ActivityModal = ({ showModal, onClose, queryParams, advanceFilters }) => {
  const [config] = useAuth();
  const [loading, setLoading] = useState(false);
  const [activityData, setActivityData] = useState([]);

  // Fetch activity data from the contact-list API
  const fetchActivityData = async () => {
    setLoading(true);

    const validAdvanceFilters = advanceFilters.filter(group =>
      group.filters && group.filters.some(f => f.field && f.operator)
    );

    const advanceFiltersQuery = encodeURIComponent(JSON.stringify(validAdvanceFilters));

    try {
      const response = await axios.get(
        `${BASE_URL}/contact-list?${queryParams}&advance_filters=${advanceFiltersQuery}`,
        config
      );

      const activities = response?.data?.data?.activity || [];
      setActivityData(activities);
    } catch (err) {
      if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
        NotificationManager.error(err.response?.data?.message || "Failed to fetch activity data");
      }
      console.error("Error fetching activity data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      fetchActivityData();
    }
  }, [showModal, queryParams, advanceFilters]);

  // Calculate total activities
  const totalActivities = activityData.reduce((sum, item) => sum + item.count, 0);

  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Get icon/emoji for each interaction type
  const getActivityIcon = (type) => {
    const icons = {
      "Conversation": "💬",
      "Email": "📧",
      "Left Message": "📞",
      "Mail": "✉️",
      "Meeting": "🤝",
      "Note": "📝",
      "Text": "💬",
    };
    return icons[type] || "📌";
  };

  // Calculate percentage
  const getPercentage = (count) => {
    if (totalActivities === 0) return 0;
    return ((count / totalActivities) * 100).toFixed(1);
  };

  return (
    <Modal
      show={showModal}
      title="Activity Overview"
      onClose={onClose}
      disableContentWrapperStyle={true}
    >
      <div className="pr-5 overflow-y-auto max-h-[70vh] mt-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader />
          </div>
        ) : activityData.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-gray-600">No activity data found</p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4 px-2">
              <span className="text-green-600 head-4 uppercase font-medium">Total Activities</span>
              <span className="text-green-600 head-3 font-bold">{formatNumber(totalActivities)}</span>
            </div>

            <hr className="border-gray-200 mb-4" />

            <div className="flex flex-col gap-4 px-2">
              {activityData.map((activity, index) => (
                <div key={index} className="flex items-center gap-4">
                  {/* Type with Icon */}
                  <div className="flex items-center gap-3 w-1/3">
                    <span className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full text-sm shadow-sm">
                      {getActivityIcon(activity.interaction_type)}
                    </span>
                    <span className="dark-H body-N truncate font-medium">{activity.interaction_type}</span>
                  </div>
                  
                  {/* Count */}
                  <div className="w-1/4 text-right pr-4">
                    <span className="dark-H body-N font-medium">{formatNumber(activity.count)}</span>
                  </div>

                  {/* Progress Bar & Percentage */}
                  <div className="flex-1 flex items-center gap-3">
                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-600 rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${getPercentage(activity.count)}%` }}
                      ></div>
                    </div>
                    <span className="dark-M body-N w-12 text-right">{Math.round(getPercentage(activity.count))}%</span>
                  </div>
                </div>
              ))}
            </div>

            <hr className="border-gray-200 mt-5" />
          </>
        )}
      </div>
    </Modal>
  );
};

export default ActivityModal;